import { useEffect, useRef, useState } from "react";

interface EditableTextProps {
  value: string;
  onChange: (v: string) => void;
  editMode?: boolean;
  as?: keyof JSX.IntrinsicElements;
  className?: string;
  style?: React.CSSProperties;
  multiline?: boolean;
  children?: React.ReactNode;
  placeholder?: string;
  "data-field"?: string;
}

/**
 * Click-to-edit inline text wrapper à la Base44.
 * - When editMode is false, renders value as plain text inside the requested tag.
 * - When editMode is true:
 *     · shows a dashed outline on hover
 *     · clicking turns the element into contentEditable
 *     · Enter (single-line) or Escape commits the change
 *     · blur commits the change
 */
export default function EditableText({
  value,
  onChange,
  editMode = false,
  as = "span",
  className = "",
  style,
  multiline = false,
  children,
  placeholder,
  ...rest
}: EditableTextProps) {
  const ref = useRef<HTMLElement>(null);
  const [editing, setEditing] = useState(false);

  // Sync DOM when value changes externally
  useEffect(() => {
    if (!editing && ref.current && ref.current.innerText !== value) {
      ref.current.innerText = value || placeholder || "";
    }
  }, [value, editing, placeholder]);

  const Tag = as as any;

  const handleClick = () => {
    if (!editMode) return;
    setEditing(true);
    setTimeout(() => {
      ref.current?.focus();
      // Select all text on first click for quick replacement
      const sel = window.getSelection();
      if (sel && ref.current) {
        const range = document.createRange();
        range.selectNodeContents(ref.current);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }, 10);
  };

  const commit = () => {
    if (!ref.current) return;
    const newVal = ref.current.innerText.trim();
    if (newVal !== value) onChange(newVal);
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      if (ref.current) ref.current.innerText = value;
      setEditing(false);
      ref.current?.blur();
    } else if (e.key === "Enter" && !multiline) {
      e.preventDefault();
      commit();
      ref.current?.blur();
    }
  };

  const editableClasses = editMode
    ? `outline-dashed outline-2 outline-offset-2 outline-transparent hover:outline-primary/60 focus:outline-primary focus:bg-primary/5 cursor-text transition-all rounded-sm ${
        editing ? "outline-primary bg-primary/5" : ""
      }`
    : "";

  const dataTestId = rest["data-field"] ? `editable-${rest["data-field"]}` : undefined;

  return (
    <Tag
      ref={ref}
      className={`${className} ${editableClasses}`.trim()}
      style={style}
      contentEditable={editMode && editing}
      suppressContentEditableWarning
      onClick={handleClick}
      onBlur={commit}
      onKeyDown={handleKeyDown}
      data-editable={editMode ? "true" : undefined}
      data-testid={dataTestId}
    >
      {children ?? value}
    </Tag>
  );
}
